/**
 * Copyright (c) Streamlit Inc. (2018-2022) Snowflake Inc. (2022-2026)
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import {
  type MouseEvent,
  type ReactElement,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react"

import { getLogger } from "loglevel"

import type { StreamlitEndpoints } from "@streamlit/connection"
import {
  isMobile,
  NavigationContext,
  SidebarConfigContext,
} from "@streamlit/lib"
import type { AppPage } from "@streamlit/protobuf"
import { localStorageAvailable, notNullOrUndefined } from "@streamlit/utils"

import NavSection from "./NavSection"
import SidebarNavLink from "./SidebarNavLink"
import {
  StyledSidebarNavContainer,
  StyledSidebarNavItems,
  StyledSidebarNavLinkListItem,
  StyledSidebarNavSeparator,
  StyledViewButton,
} from "./styled-components"
import {
  filterVisiblePages,
  getExternalPageUrl,
  groupPagesBySection,
  isExternalPage,
  processNavigationStructure,
} from "./utils"

export interface Props {
  endpoints: StreamlitEndpoints
  collapseSidebar: () => void
  hasSidebarElements: boolean
  widgetsDisabled: boolean
}

// Default number of pages to show when the sidebar is collapsed.
const NUM_PAGES_TO_SHOW_WHEN_COLLAPSED = 10

const LOG = getLogger("SidebarNav")

interface NavLinkProps {
  pageUrl: string
  page: AppPage.$Properties
  isActive: boolean
  onClick: (e: MouseEvent) => void
  widgetsDisabled: boolean
}

function NavLink({
  pageUrl,
  page,
  isActive,
  onClick,
  widgetsDisabled,
}: NavLinkProps): ReactElement {
  const pageName = page.pageName as string

  return (
    <StyledSidebarNavLinkListItem>
      <SidebarNavLink
        isActive={isActive}
        pageUrl={pageUrl}
        icon={page.icon}
        onClick={onClick}
        widgetsDisabled={widgetsDisabled}
        isExternal={isExternalPage(page)}
        externalUrl={getExternalPageUrl(page)}
      >
        {pageName}
      </SidebarNavLink>
    </StyledSidebarNavLinkListItem>
  )
}

function generateNavSections(
  sections: Record<string, AppPage.$Properties[]>,
  needsCollapse: boolean,
  generateNavLink: (page: AppPage.$Properties, index: number) => ReactElement,
  expandedSections: Record<string, boolean>,
  toggleSection: (section: string) => void,
  currentPageCount: number,
  numPagesToShowWhenCollapsed: number
): { sections: ReactNode[]; updatedPageCount: number } {
  const contents: ReactNode[] = []
  let pageCount = currentPageCount

  Object.entries(sections).forEach(([header, pages]) => {
    // Create a shallow copy to prevent mutations below from affecting
    // the original array.
    const sectionPages = [...pages]
    let viewablePages = sectionPages
    const isExpanded = expandedSections[header]

    if (needsCollapse) {
      const availableSlots = numPagesToShowWhenCollapsed - pageCount
      if (availableSlots <= 0) {
        viewablePages = []
      } else if (sectionPages.length > availableSlots) {
        // We can partially show the section
        viewablePages = sectionPages.slice(0, availableSlots)
      }
    }

    if (isExpanded) {
      pageCount += viewablePages.length
    }

    if (viewablePages.length > 0) {
      contents.push(
        <NavSection
          key={header}
          header={header}
          isExpanded={isExpanded}
          onToggle={() => toggleSection(header)}
        >
          {viewablePages.map(generateNavLink)}
        </NavSection>
      )
    }
  })

  return { sections: contents, updatedPageCount: pageCount }
}

/** Displays a list of navigable app page links for multi-page apps. */
const SidebarNav = ({
  endpoints,
  collapseSidebar,
  hasSidebarElements,
  widgetsDisabled,
}: Props): ReactElement | null => {
  const [expanded, setExpanded] = useState(false)
  const { expandSidebarNav, sidebarNavVisibleItems } = useContext(
    SidebarConfigContext
  )
  const { pageLinkBaseUrl, appPages, onPageChange, currentPageScriptHash } =
    useContext(NavigationContext)

  // Use the value from context if provided, otherwise use the default
  const numPagesToShowWhenCollapsed =
    notNullOrUndefined(sidebarNavVisibleItems) && sidebarNavVisibleItems > 0
      ? sidebarNavVisibleItems
      : NUM_PAGES_TO_SHOW_WHEN_COLLAPSED
  // The collapse threshold is numPagesToShowWhenCollapsed + 2
  const collapseThreshold = numPagesToShowWhenCollapsed + 2

  const localStorageKey = `stSidebarSectionsState-${pageLinkBaseUrl}`
  const [expandedSections, setExpandedSections] = useState<Record<
    string,
    boolean
  > | null>(null)

  // Filter out hidden pages for display purposes
  const visiblePages = useMemo(() => filterVisiblePages(appPages), [appPages])

  const navigationStructure = useMemo(() => {
    return processNavigationStructure(groupPagesBySection(visiblePages))
  }, [visiblePages])

  const numVisiblePages = useMemo(() => {
    const hasSections = Object.keys(navigationStructure.sections).length > 0

    if (!hasSections) {
      return visiblePages.length
    }

    let count = navigationStructure.individualPages.length

    // Only count pages in expanded sections
    Object.entries(navigationStructure.sections).forEach(
      ([sectionName, pages]) => {
        if (expandedSections?.[sectionName]) {
          count += pages.length
        }
      }
    )

    return count
  }, [visiblePages.length, expandedSections, navigationStructure])

  useEffect(() => {
    const cachedSidebarNavExpanded =
      localStorageAvailable() &&
      window.localStorage.getItem("sidebarNavState") === "expanded"

    if (!expanded && (expandSidebarNav || cachedSidebarNavExpanded)) {
      setExpanded(true)
    }
  }, [expanded, expandSidebarNav])

  // Loading the state of sections (expanded/collapsed) from localStorage:
  useEffect(() => {
    let storedState: Record<string, boolean> = {}
    if (localStorageAvailable()) {
      const stored = window.localStorage.getItem(localStorageKey)
      if (stored) {
        try {
          storedState = JSON.parse(stored)
        } catch (e) {
          LOG.warn("Could not parse sidebar nav state from localStorage", e)
        }
      }
    }

    const allSections = Object.keys(navigationStructure.sections).reduce<
      Record<string, boolean>
    >((acc, sectionName) => {
      // Default to expanded if not in stored state
      acc[sectionName] = storedState[sectionName] ?? true
      return acc
    }, {})

    setExpandedSections(allSections)
  }, [navigationStructure.sections, localStorageKey])

  // Store the current expanded sections state in localStorage:
  useEffect(() => {
    if (localStorageAvailable() && expandedSections) {
      window.localStorage.setItem(
        localStorageKey,
        JSON.stringify(expandedSections)
      )
    }
  }, [expandedSections, localStorageKey])

  const toggleSection = useCallback((sectionName: string) => {
    setExpandedSections(prev => {
      if (!prev) {
        return null
      }
      return {
        ...prev,
        [sectionName]: !prev[sectionName],
      }
    })
  }, [])

  const handleViewButtonClick = useCallback(() => {
    const nextState = !expanded
    if (localStorageAvailable()) {
      if (nextState) {
        window.localStorage.setItem("sidebarNavState", "expanded")
      } else {
        window.localStorage.removeItem("sidebarNavState")
      }
    }
    setExpanded(nextState)
  }, [expanded])

  const generateNavLink = useCallback(
    (page: AppPage.$Properties, index: number) => {
      const pageUrl = endpoints.buildAppPageURL(pageLinkBaseUrl, page)
      const isActive = page.pageScriptHash === currentPageScriptHash
      const isExternal = isExternalPage(page)

      return (
        <NavLink
          key={`${page.pageName}-${index}`}
          pageUrl={pageUrl}
          page={page}
          isActive={isActive}
          onClick={e => {
            // External links are handled by the browser (target="_blank")
            if (isExternal) {
              // Still collapse sidebar on mobile for external links
              if (isMobile()) {
                collapseSidebar()
              }
              return
            }
            e.preventDefault()
            onPageChange(page.pageScriptHash as string)
            if (isMobile()) {
              collapseSidebar()
            }
          }}
          widgetsDisabled={widgetsDisabled}
        />
      )
    },
    [
      collapseSidebar,
      currentPageScriptHash,
      endpoints,
      onPageChange,
      pageLinkBaseUrl,
      widgetsDisabled,
    ]
  )

  if (!expandedSections) {
    // Return null while waiting for the expanded sections state to be initialized
    // to avoid a flicker on the first render.
    return null
  }

  const contents: ReactNode[] = []
  const shouldShowViewButton =
    hasSidebarElements &&
    numVisiblePages > collapseThreshold &&
    !expandSidebarNav
  const needsCollapse = shouldShowViewButton && !expanded

  // First, render individual pages (those with empty section headers)
  let currentPageCount = 0
  if (navigationStructure.individualPages.length > 0) {
    const individualPages = needsCollapse
      ? navigationStructure.individualPages.slice(
          0,
          numPagesToShowWhenCollapsed
        )
      : navigationStructure.individualPages
    contents.push(...individualPages.map(generateNavLink))
    currentPageCount += individualPages.length
  }

  // Then, render sections if there are any
  if (Object.keys(navigationStructure.sections).length > 0) {
    const result = generateNavSections(
      navigationStructure.sections,
      needsCollapse,
      generateNavLink,
      expandedSections,
      toggleSection,
      currentPageCount,
      numPagesToShowWhenCollapsed
    )
    contents.push(...result.sections)
  }

  return (
    <StyledSidebarNavContainer data-testid="stSidebarNav">
      <StyledSidebarNavItems data-testid="stSidebarNavItems">
        {contents}
      </StyledSidebarNavItems>
      {shouldShowViewButton && (
        <StyledViewButton
          onClick={handleViewButtonClick}
          data-testid="stSidebarNavViewButton"
        >
          {expanded
            ? "View less"
            : `View ${numVisiblePages - numPagesToShowWhenCollapsed} more`}
        </StyledViewButton>
      )}
      {hasSidebarElements && (
        <StyledSidebarNavSeparator data-testid="stSidebarNavSeparator" />
      )}
    </StyledSidebarNavContainer>
  )
}

export default SidebarNav
